/**
 * Universal Price Resolver & Guest Calculator for Ganesh Holiday Homes
 * Handles:
 * - Dynamic room price resolution from price_map
 * - Dynamic per_person_price_map resolution for extra guests
 * - Dynamic room limits (no-cost limit & max guest capacity) from backend config
 * - Smart room type matching & normalization across query params and config API
 */

export const DEFAULT_ROOM_PRICES = {
  "Standard Ac Room": 2500,
  "Standard AC Room": 2500,
  "Standard Non-Ac Room": 2000,
  "Standard Non-AC Room": 2000,
  "Double Ac Room": 4800,
  "Double AC Room": 4800,
  "Double Room AC": 4800,
  "Double Non-Ac Room": 4000,
  "Double Non-AC Room": 4000,
  "Double Room Non-AC": 4000,
};

export const DEFAULT_PER_PERSON_PRICES = {
  "Double Ac Room": 200,
  "Double Non-Ac Room": 200,
  "Standard Ac Room": 200,
  "Standard Non-Ac Room": 200,
};

export function isRoomTypeMatch(type1, type2) {
  if (!type1 || !type2) return false;
  if (type1 === type2) return true;
  const t1 = String(type1).trim().toLowerCase();
  const t2 = String(type2).trim().toLowerCase();
  if (t1 === t2) return true;
  if (t1.replace(/[^a-z0-9]/g, "") === t2.replace(/[^a-z0-9]/g, "")) return true;

  const isDouble1 = t1.includes("double");
  const isDouble2 = t2.includes("double");
  const isNonAc1 = t1.includes("non");
  const isNonAc2 = t2.includes("non");
  return isDouble1 === isDouble2 && isNonAc1 === isNonAc2;
}

export function normalizeRoomType(roomType, availableTypes = []) {
  if (!roomType) return availableTypes[0] || "Standard AC Room";
  if (Array.isArray(availableTypes) && availableTypes.length > 0) {
    const match = availableTypes.find((t) => isRoomTypeMatch(t, roomType));
    if (match) return match;
  }
  return roomType;
}

export function getRoomCardDetails(roomType) {
  const t = String(roomType || "").toLowerCase();
  const isDouble = t.includes("double");
  const isNonAc = t.includes("non");

  if (isDouble) {
    return {
      price: isNonAc ? 4000 : 4800,
      maxGuests: 4,
      bed: "2 Double Beds",
      features: isNonAc
        ? "Large Family Room, Natural Breeze"
        : "Air Conditioned, Balcony, Family Suite",
      tag: isNonAc ? "Spacious Family" : "Family Favorite",
    };
  }

  return {
    price: isNonAc ? 2000 : 2500,
    maxGuests: 2,
    bed: "1 King Bed",
    features: isNonAc
      ? "Natural Breeze, Attached Clean Bath"
      : "Air Conditioned, Attached Bath, Garden View",
    tag: isNonAc ? "Budget Comfort" : "Deluxe Comfort",
  };
}

export function resolvePriceFromMap(priceMap, roomIdentifier, defaultFallback = null) {
  const raw = String(roomIdentifier || "").trim();
  if (!raw) return defaultFallback || 2500;

  const map = priceMap && typeof priceMap === "object" ? priceMap : {};

  // 1. Direct exact match
  if (map[raw] !== undefined && map[raw] !== null && !isNaN(Number(map[raw]))) {
    return Number(map[raw]);
  }

  // 2. Case-insensitive exact string match
  const rawLower = raw.toLowerCase();
  for (const [key, val] of Object.entries(map)) {
    if (key.toLowerCase().trim() === rawLower) {
      return Number(val);
    }
  }

  // 3. Clean alphanumeric match (ignoring dashes, spaces, underscores)
  const cleanTarget = rawLower.replace(/[^a-z0-9]/g, "");
  for (const [key, val] of Object.entries(map)) {
    const cleanKey = key.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (cleanKey === cleanTarget) {
      return Number(val);
    }
  }

  // 4. Semantic Category Match (Handles word order differences like "Double Room AC" vs "Double Ac Room")
  const isDouble = rawLower.includes("double");
  const isStandard = rawLower.includes("standard") || (!isDouble && (rawLower.includes("single") || rawLower.includes("ac")));
  const isNonAc = rawLower.includes("non");
  const isAc = !isNonAc && (rawLower.includes("ac") || rawLower.includes("air"));

  for (const [key, val] of Object.entries(map)) {
    const k = key.toLowerCase();
    const kIsDouble = k.includes("double");
    const kIsStandard = k.includes("standard");
    const kIsNonAc = k.includes("non");
    const kIsAc = !kIsNonAc && k.includes("ac");

    if (isDouble && kIsDouble) {
      if (isNonAc && kIsNonAc) return Number(val);
      if (isAc && kIsAc) return Number(val);
    }

    if (isStandard && kIsStandard) {
      if (isNonAc && kIsNonAc) return Number(val);
      if (isAc && kIsAc) return Number(val);
    }
  }

  // 5. Fallback from DEFAULT_ROOM_PRICES with same semantic matching
  if (defaultFallback !== null && defaultFallback !== undefined) {
    return defaultFallback;
  }

  if (isDouble) {
    return isNonAc ? 4000 : 4800;
  }

  return isNonAc ? 2000 : 2500;
}

/**
 * Resolves guest limits based on room type and config API
 */
export function getRoomLimits(configData, roomIdentifier) {
  const raw = String(roomIdentifier || "").toLowerCase();
  const isDouble = raw.includes("double");

  if (isDouble) {
    return {
      isDouble: true,
      noCostLimit: Number(configData?.double_room_no_cost_limit ?? 7),
      maxLimit: Number(configData?.double_room_max_limit ?? 9),
    };
  }

  return {
    isDouble: false,
    noCostLimit: Number(configData?.standard_room_no_cost_limit ?? 2),
    maxLimit: Number(configData?.standard_room_max_limit ?? 4),
  };
}

/**
 * Resolves maximum allowable room count for a specific room type based on property config
 * e.g. Double Room = 1, Standard Room = 5
 */
export function getMaxRoomsForType(configData, roomIdentifier) {
  const raw = String(roomIdentifier || "").toLowerCase();
  const isDouble = raw.includes("double");

  if (isDouble) {
    return Math.max(1, Number(configData?.double_room_count ?? 1));
  }

  return Math.max(1, Number(configData?.standard_room_count ?? 5));
}

/**
 * Resolves per-person extra rate from configData.per_person_price_map
 */
export function resolvePerPersonPrice(perPersonPriceMap, roomIdentifier, defaultFallback = 200) {
  return resolvePriceFromMap(perPersonPriceMap || DEFAULT_PER_PERSON_PRICES, roomIdentifier, defaultFallback);
}

/**
 * Universal Total Bill Calculator including room rate & extra person charges
 */
export function calculateBookingTotal({
  configData,
  roomIdentifier,
  roomCount = 1,
  guestCount = 2,
  nights = 1,
  roomPricesOverride = null,
}) {
  const baseRate = resolvePriceFromMap(configData?.price_map || roomPricesOverride, roomIdentifier, 2500);
  const limits = getRoomLimits(configData, roomIdentifier);
  const perPersonExtraRate = resolvePerPersonPrice(configData?.per_person_price_map, roomIdentifier, 200);

  const numRooms = Math.max(1, Number(roomCount) || 1);
  const numGuests = Math.max(1, Number(guestCount) || 1);
  const numNights = Math.max(1, Number(nights) || 1);

  const totalNoCostCapacity = limits.noCostLimit * numRooms;
  const maxAllowedCapacity = limits.maxLimit * numRooms;
  const extraGuests = Math.max(0, numGuests - totalNoCostCapacity);
  const extraCostPerNight = extraGuests * perPersonExtraRate;

  const nightlyRateTotal = (baseRate * numRooms) + extraCostPerNight;
  const totalAmount = nightlyRateTotal * numNights;

  return {
    baseRate,
    extraGuests,
    perPersonExtraRate,
    extraCostPerNight,
    nightlyRateTotal,
    totalAmount,
    limits,
    totalNoCostCapacity,
    maxAllowedCapacity,
    isExceedingMax: numGuests > maxAllowedCapacity,
  };
}

/**
 * Resolves advance token deposit amount per room per night from configData
 */
export function resolveAdvanceBookingAmount(configData, defaultFallback = 1000) {
  if (!configData) return defaultFallback;
  const val = Number(
    configData.advanceBookingAmount ??
    configData.baseBookingAmount ??
    configData.bookingAmountPerRoom ??
    configData.booking_amount ??
    defaultFallback
  );
  return isNaN(val) || val <= 0 ? defaultFallback : val;
}
